import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Slide from '@mui/material/Slide';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';

type ToastKind = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  description?: string;
  undo?: { label: string; onClick: () => void };
  /** Auto-dismiss after this many ms. 0 = sticky. */
  durationMs?: number;
}

interface ToastContextValue {
  toast: (kind: ToastKind, message: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'message'>>) => void;
  success: (message: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'message'>>) => void;
  error: (message: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'message'>>) => void;
  info: (message: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'message'>>) => void;
  warning: (message: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'message'>>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICON: Record<ToastKind, ReactNode> = {
  success: <CheckCircleIcon sx={{ color: '#22C55E' }} />,
  error:   <ErrorIcon       sx={{ color: '#EF4444' }} />,
  info:    <InfoIcon        sx={{ color: '#3B82F6' }} />,
  warning: <WarningIcon     sx={{ color: '#F59E0B' }} />,
};

const ACCENT: Record<ToastKind, string> = {
  success: '#22C55E',
  error:   '#EF4444',
  info:    '#3B82F6',
  warning: '#F59E0B',
};

const MAX_VISIBLE = 3;

/** Global toast queue. Wrap the app in ToastProvider, then call
 * useToast() anywhere for `success/error/info/warning(message, {undo, durationMs, description})`. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setQueue((q) => q.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((kind: ToastKind, message: string, opts: Partial<Omit<Toast, 'id' | 'kind' | 'message'>> = {}) => {
    const id = Date.now() + Math.random();
    const next: Toast = { id, kind, message, ...opts };
    setQueue((q) => [...q.slice(-(MAX_VISIBLE - 1)), next]);
    const duration = opts.durationMs ?? (kind === 'error' ? 8000 : 4500);
    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }
  }, [dismiss]);

  const ctx: ToastContextValue = {
    toast: push,
    success: (m, o) => push('success', m, o),
    error:   (m, o) => push('error', m, o),
    info:    (m, o) => push('info', m, o),
    warning: (m, o) => push('warning', m, o),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <Box
        role="region"
        aria-label="התראות"
        aria-live="polite"
        sx={{
          position: 'fixed',
          bottom: 24,
          insetInlineEnd: 24,
          zIndex: 1600,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: 380,
          maxWidth: 'calc(100vw - 48px)',
          pointerEvents: 'none',
        }}
      >
        {queue.map((t) => (
          <Slide key={t.id} direction="left" in mountOnEnter unmountOnExit>
            <Box
              role={t.kind === 'error' ? 'alert' : 'status'}
              sx={{
                pointerEvents: 'auto',
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderInlineStart: `3px solid ${ACCENT[t.kind]}`,
                boxShadow: '0 14px 32px -10px rgba(15,23,42,0.24)',
                p: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
              }}
            >
              <Box sx={{ pt: 0.25 }}>{ICON[t.kind]}</Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>
                  {t.message}
                </Typography>
                {t.description && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                    {t.description}
                  </Typography>
                )}
                {t.undo && (
                  <Button
                    size="small"
                    sx={{ mt: 0.5, p: 0, minWidth: 'auto', fontSize: 12, color: ACCENT[t.kind] }}
                    onClick={() => {
                      t.undo!.onClick();
                      dismiss(t.id);
                    }}
                  >
                    {t.undo.label}
                  </Button>
                )}
              </Box>
              <IconButton size="small" sx={{ p: 0.25 }} onClick={() => dismiss(t.id)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Slide>
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback that warns the dev but doesn't crash the app — useful
    // during incremental adoption.
    const fallback: ToastContextValue = {
      toast: (kind, m) => window.alert(`[${kind}] ${m}`),
      success: (m) => window.alert(m),
      error: (m) => window.alert(m),
      info: (m) => window.alert(m),
      warning: (m) => window.alert(m),
    };
    return fallback;
  }
  return ctx;
}
